// src/components/profile/ProfileDisplayCard.tsx
"use client";

import type { ProfileData } from "@/types/profile";
import { isIdentityVerified } from '@/lib/verification/verificationRequirements';import {
  getPublishableStreetAddress,
  mayPublishStreetAddress,
} from '@/lib/profile/streetAddressVisibility';
import { isRetailProfile } from '@/lib/profile/verticalMatchers';
import { evaluateRetailTradingGate } from '@/lib/retailSales/retailTradingGate';
import { RetailGoLiveBadge } from '@/components/storefront/RetailGoLiveBadge';
import { FOUNDER_BADGE_LABEL, isFounderProfileType } from "@/lib/founders/founderPlan";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import Image from "next/image";
import { SquadSpecsBar } from "@/components/fix-it/SquadSpecsBar";
import { VerificationBadges } from "@/components/profile/TrustBadgeStrip";
import { Phone, Zap, Clock } from "lucide-react";
import {
  Briefcase,
  Building,
  UserCircle,
  Mail,
  MapPin,
  MessageSquare,
  Info,
  ExternalLink,
  GraduationCap,
  Edit3,
  Eye,
  ChevronDown,
  ChevronUp,
  Calendar,
  Users,
  Award,
  Lightbulb,
  Globe,
  CheckCircle
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { incrementProfileView, incrementWhatsAppClick, incrementBookingCtaClick } from "@/lib/firebase/services/profilesService";
import { resolveLeadProductFromProfileType } from "@/lib/leads/leadEventSchema";
import { logContactClick } from "@/lib/leads/logContactClick";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useFirebase } from "@/firebase/provider";
import React from "react";
import { useRouter } from "next/navigation";

// A robust helper function to safely render any data type as a string.
const safelyRender = (data: any): string => {
  if (data === null || data === undefined) {
    return '';
  }
  if (typeof data === 'string') {
    return data;
  }
  if (Array.isArray(data)) {
    // Handles arrays of strings or objects
    return data.map(item => safelyRender(item)).join(', ');
  }
  if (typeof data === 'object') {
    // If an object has a 'label' or 'name', it's likely a structured field.
    if (data.label) return String(data.label);
    if (data.name) return String(data.name);
    // Fallback for other objects to prevent crashing.
    return JSON.stringify(data);
  }
  // Fallback for numbers, booleans, etc.
  return String(data);
};

const getPricingSummary = (profileData: ProfileData) => {
  if (!profileData.showPricing || !Array.isArray(profileData.pricingServices)) return null;
  const normalized = profileData.pricingServices
    .map(service => {
      const minPrice = typeof service.minPrice === 'number' ? service.minPrice : Number(service.minPrice);
      const maxPrice = typeof service.maxPrice === 'number' ? service.maxPrice : Number(service.maxPrice);
      return {
        name: service.name,
        minPrice: Number.isFinite(minPrice) ? minPrice : undefined,
        maxPrice: Number.isFinite(maxPrice) ? maxPrice : undefined,
        unit: service.unit,
      };
    })
    .filter(service => service.name);

  if (normalized.length === 0) return null;

  const cheapest = normalized.reduce((acc, service) => {
    const candidate = service.minPrice ?? service.maxPrice ?? acc.price;
    if (candidate === undefined) return acc;
    if (acc.price === undefined || candidate < acc.price) {
      return { price: candidate, unit: service.unit };
    }
    return acc;
  }, { price: undefined as number | undefined, unit: undefined as string | undefined });

  if (cheapest.price === undefined) return null;

  return {
    display: `From R${cheapest.price}${cheapest.unit ? ` / ${cheapest.unit}` : ''}`,
    count: normalized.length,
  };
};

const normalizeGoogleMapsUrl = (url?: string): string | null => {
  if (typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  return trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
};

const getGoogleMapsUrl = (profileData: ProfileData): string | null => {
  const directLink = normalizeGoogleMapsUrl(profileData.googleMapsLink);
  if (directLink) return directLink;

  const addressParts = [
    profileData.physicalAddress,
    profileData.suburb,
    profileData.cityTown,
    profileData.province,
  ]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean);

  if (addressParts.length === 0) return null;
  const query = encodeURIComponent(addressParts.join(", "));
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
};

const DEFAULT_BOOKING_TIMEZONE = "Africa/Johannesburg";
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const resolveTimezone = (timeZone?: string | null) => {
  if (typeof timeZone !== "string" || !timeZone.trim()) {
    return DEFAULT_BOOKING_TIMEZONE;
  }
  try {
    Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch {
    return DEFAULT_BOOKING_TIMEZONE;
  }
};

const getZonedDateParts = (date: Date, timeZone: string) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const valueOf = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  const dayName = valueOf("weekday");
  const hours = Number(valueOf("hour"));
  const minutes = Number(valueOf("minute"));
  const dateKey = `${valueOf("year")}-${valueOf("month")}-${valueOf("day")}`;
  return {
    dayName,
    minutesSinceMidnight: hours * 60 + minutes,
    dateKey,
  };
};

const timeStringToMinutes = (value?: string | null): number | null => {
  if (typeof value !== "string") {
    return null;
  }
  const [hourString, minuteString] = value.split(":");
  const hours = Number(hourString);
  const minutes = Number(minuteString);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }
  return hours * 60 + minutes;
};

const resolveDateValue = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const resolved = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(resolved.getTime()) ? null : resolved;
  }
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatFreshnessLabel = (value: unknown): string | null => {
  const resolved = resolveDateValue(value);
  if (!resolved) return null;
  const diffDays = Math.floor((Date.now() - resolved.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 1) return 'Updated recently';
  if (diffDays <= 7) return 'Updated this week';
  if (diffDays <= 30) return 'Updated this month';
  return 'Public profile available';
};

const formatAvailabilityStamp = (value: unknown): string | null => {
  const resolved = resolveDateValue(value);
  if (!resolved) return null;
  const diffMinutes = Math.max(1, Math.round((Date.now() - resolved.getTime()) / 60000));
  if (diffMinutes < 60) return `Updated ${diffMinutes} min ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `Updated ${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `Updated ${diffDays}d ago`;
  return `Updated ${resolved.toLocaleDateString()}`;
};

const isVerifiedStatus = (value: unknown): boolean => value === 'Verified' || value === true;
const isPendingStatus = (value: unknown): boolean => value === 'Pending' || value === 'Pending Review' || value === 'In Process';

const splitPhrases = (value: unknown): string[] => {
  if (typeof value !== 'string') return [];
  return value
    .split(/[|,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
};

interface ProfileDisplayCardProps {
  profileData: ProfileData;
  searchTerm?: string;
  selectedService?: string;
  bestValueUserIds?: string[];
  outlierInfo?: { low: string[]; high: string[] };
  resultReasons?: string[];
  onProfileNavigate?: (profileId: string) => void;
  showFeaturedFounderBadge?: boolean;
  canLeaveReview?: boolean;
}

const DetailItem = ({ icon, label, value, isList = false, listAsBadges = true, isLink = false }: { icon: React.ReactNode, label:string, value?: any, isList?: boolean, listAsBadges?: boolean, isLink?: boolean }) => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return null;
  }

  let items: string[] = [];
  
  if (isList) {
    const rawItems = Array.isArray(value) ? value : String(value).split(',');
    items = rawItems.map(item => safelyRender(item).trim()).filter(Boolean);
    if (items.length === 0) return null;
  }

  const renderedValue = isList ? items.join(', ') : safelyRender(value);

  if (!renderedValue && items.length === 0) return null;

  let url = '#';
  if (isLink && typeof renderedValue === 'string' && renderedValue.trim()) {
    const trimmedUrl = renderedValue.trim();
    // Universal fix: if it doesn't start with http, prepend https://
    url = trimmedUrl.startsWith('http') ? trimmedUrl : `https://${trimmedUrl}`;
  }

  return (
    <div className="mb-1">
      <h4 className="text-xs font-semibold text-primary mb-0.5 flex items-center">
        {icon} <span className="ml-1.5">{label}</span>
      </h4>
      {isLink ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline break-words [overflow-wrap:anywhere] whitespace-pre-wrap">
          {renderedValue}
        </a>
      ) : isList && listAsBadges ? (
        <div className="flex flex-wrap gap-1">
          {items.map((item, idx) => <Badge key={idx} variant="secondary" className="text-[10px] px-1.5 py-0.5">{item}</Badge>)}
        </div>
      ) : (
        <p className="text-xs text-foreground break-words [overflow-wrap:anywhere] whitespace-pre-wrap">{renderedValue}</p>
      )}
    </div>
  );
};

const SubCard = ({ title, children, className }: { title?: string, children: React.ReactNode, className?: string}) => (
  <Card className={`shadow-xs mb-2 bg-(--sub-card)/70 ${className}`}>
    {title && 
      <CardHeader className="p-1.5 pb-0 bg-(--sub-card)/70">
        <CardTitle className="text-xs font-semibold text-foreground bg-(--sub-card)/70">{title}</CardTitle>
      </CardHeader>
    }
    <CardContent className={`bg-(--sub-card)/70 p-1.5 text-xs min-w-0 ${!title && 'pt-1.5'}`}>
      {children}
    </CardContent>
  </Card>
);




// Add props for review summary
export function ProfileDisplayCard({ profileData, searchTerm, selectedService, bestValueUserIds, outlierInfo, resultReasons, onProfileNavigate, showFeaturedFounderBadge = false, canLeaveReview = true, reviewSummary }: ProfileDisplayCardProps & { reviewSummary?: { averageRating: number, reviewCount: number, profileOwnerName: string, profileId: string } }) {
        // --- Social Links Section ---
        const socialLinks: Array<{ label: string; value?: string; icon: React.ReactNode }> = [
          { label: 'Website', value: profileData.website, icon: <ExternalLink size={14} /> },
          { label: 'Founder Profile', value: profileData.founderSocialProfileUrl, icon: <ExternalLink size={14} /> },
          { label: 'Facebook', value: profileData.facebookPageUrl, icon: <ExternalLink size={14} /> },
          { label: 'Instagram', value: profileData.instagramUrl, icon: <ExternalLink size={14} /> },
          { label: 'LinkedIn', value: profileData.linkedinUrl, icon: <ExternalLink size={14} /> },
          { label: 'YouTube', value: profileData.youtubeUrl, icon: <ExternalLink size={14} /> },
          { label: 'Google Business', value: profileData.googleBusinessProfileUrl, icon: <ExternalLink size={14} /> },
        ].filter(link => typeof link.value === 'string' && link.value.trim().length > 0);
      // --- Top Reasons to Choose logic ---
      function getTopReasons(): string[] {
        // Use resultReasons if provided and non-empty
        if (Array.isArray(resultReasons) && resultReasons.length > 0) {
          return resultReasons.slice(0, 3);
        }
        const reasons: string[] = [];
        // 1. Strong reviews
        if (profileData.reviewCount && profileData.reviewCount > 2 && profileData.averageRating && profileData.averageRating >= 4.5) {
          reasons.push('Excellent reviews');
        } else if (profileData.reviewCount && profileData.reviewCount > 0 && profileData.averageRating && profileData.averageRating >= 4.0) {
          reasons.push('Great customer feedback');
        }
        // 2. Verified/trust
        if (isIdentityVerified(profileData) || (Array.isArray(profileData.manualBadges) && profileData.manualBadges.length > 0)) {
          reasons.push('Verified & trusted');
        }
        // 3. Fast response or high engagement
        if (profileData.profileViews && profileData.profileViews > 100) {
          reasons.push('Popular with clients');
        }
        if (profileData.whatsAppClicks && profileData.whatsAppClicks > 20) {
          reasons.push('Highly responsive');
        }
        // 4. Complete profile
        if (profileData.description && profileData.skills && profileData.cityTown && profileData.profilePhotoUrl) {
          reasons.push('Complete profile');
        }
        // 5. Hiring now
        if (profileData.isHiringNow) {
          reasons.push('Hiring now');
        }
        // 6. Featured/paid
        if (showFeaturedFounderBadge) {
          reasons.push('Featured founder');
        }
        // 7. Fallback
        if (reasons.length === 0) {
          reasons.push('Trusted on Zamzi Connect');
        }
        return reasons.slice(0, 3);
      }
    // --- User Price Fairness Ratings (client-side demo) ---
    const ratingKey = `priceRating:${profileData.userId}:${selectedService || 'all'}`;
    const [userRating, setUserRating] = useState<string | null>(() => {
      if (typeof window !== 'undefined') {
        return localStorage.getItem(ratingKey);
      }
      return null;
    });
    const [ratings, setRatings] = useState<{ [k: string]: number }>(() => {
      if (typeof window !== 'undefined') {
        const raw = localStorage.getItem('priceRatingsSummary');
        return raw ? JSON.parse(raw) : {};
      }
      return {};
    });
    const googleMapsUrl = getGoogleMapsUrl(profileData);
    // Move refs to top


    const handleRating = (rating: string) => {
      setUserRating(rating);
      if (typeof window !== 'undefined') {
        localStorage.setItem(ratingKey, rating);
        // Update summary
        const raw = localStorage.getItem('priceRatingsSummary');
        const summary = raw ? JSON.parse(raw) : {};
        const key = `${profileData.userId}:${selectedService || 'all'}`;
        summary[key] = summary[key] || { Fair: 0, Expensive: 0, Bargain: 0 };
        summary[key][rating] = (summary[key][rating] || 0) + 1;
        localStorage.setItem('priceRatingsSummary', JSON.stringify(summary));
        setRatings(summary[key]);
      }
    };

    // Get summary for this profile/service
    let ratingSummary = { Fair: 0, Expensive: 0, Bargain: 0 };
    if (typeof window !== 'undefined') {
      const raw = localStorage.getItem('priceRatingsSummary');
      if (raw) {
        const summary = JSON.parse(raw);
        const key = `${profileData.userId}:${selectedService || 'all'}`;
        if (summary[key]) ratingSummary = summary[key];
      }
    }
  const { auth } = useFirebase();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const router = useRouter();
  const publicProfileUrl = `/profile/${profileData.userId}${searchTerm ? `?from_search=${encodeURIComponent(searchTerm)}` : ''}`;
  const resolvedBookingMode =
    profileData.bookingMode === 'instant' ||
    (profileData.bookingMode !== 'manual' && Boolean(profileData.stayInstantBooking))
      ? 'instant'
      : 'manual';
  const bookingSectionUrl = `${publicProfileUrl}#${resolvedBookingMode === 'instant' ? 'instant-book' : 'booking-request'}`;
  const enquirySectionUrl = `${publicProfileUrl}#lead-inquiry`;

    const normalizedProfileType = safelyRender(profileData.profileType).toLowerCase();
  const isBusiness =
    normalizedProfileType.includes("business") ||
    normalizedProfileType.includes("restaurant") ||
    normalizedProfileType.includes("fix-it") ||
    normalizedProfileType.includes("stay") ||
    normalizedProfileType.includes("hospitality") ||
    normalizedProfileType.includes("accommodation") ||
    normalizedProfileType.includes("shopping") ||
    normalizedProfileType.includes("retail");
  const isFreeProfile = ["Job Seeker (Free)", "Business Owner (Free)"].includes(safelyRender(profileData.profileType));
  const isPaidBusinessFounder = (normalizedProfileType.includes("business") || normalizedProfileType.includes("restaurant") || normalizedProfileType.includes("shopping") || normalizedProfileType.includes("retail")) && !normalizedProfileType.includes("free");
  const isFixIt = normalizedProfileType.includes("fix-it");
  const isRestaurant = normalizedProfileType.includes("restaurant");

  // Retail trading gate: an unverified Shopaholic store may not take money, so
  // the result card says so before a shopper clicks through and finds an
  // unopen storefront. Derived, never stored - it clears the moment the
  // seller's verification completes.
  const isRetailCard = isRetailProfile(profileData);
  const retailTradingGate = isRetailCard ? evaluateRetailTradingGate(profileData) : null;
  // While the gate is closed the WhatsApp number is not published anywhere on
  // the card - the number is the sales channel the gate exists to close.
  const isRetailGated = Boolean(retailTradingGate && !retailTradingGate.open);

  const genericProfileType = isBusiness ? "Business" : "Job Seeker";

  const displayName = isBusiness ? profileData.businessName : profileData.username;
  const displaySummary = isBusiness ? profileData.description : profileData.professionalSummary;
  const pricingSummary = isBusiness ? getPricingSummary(profileData) : null;
  const showHiringNowBadge = Boolean(profileData.isHiringNow);
  const verifiedSignalsCount = [
    profileData.huru_clearance_status,
    profileData.id_document_status,
    profileData.cipc_registration_status,
    profileData.coa_certificate_status,
    profileData.sars_clearance_status,
    profileData.pirb_registration_status,
    profileData.nhbrc_certificate_status,
    profileData.saqcc_gas_practitioner_status,
    profileData.electrical_coc_status,
    profileData.gas_coc_status,
    profileData.plumbing_coc_status,
    profileData.electric_fence_coc_status,
    profileData.water_installation_certificate_status,
    profileData.public_liability_insurance_status,
    profileData.qualification_certificate_status,
    profileData.ffc_certificate_status,
    profileData.fsp_license_status,
    profileData.sars_pr_number_status,
    profileData.red_seal_certificate_status,
    profileData.repssa_registration_status,
    profileData.emailVerified,
  ].filter(isVerifiedStatus).length;
  const pendingSignalsCount = [
    profileData.huru_clearance_status,
    profileData.id_document_status,
    profileData.cipc_registration_status,
    profileData.coa_certificate_status,
    profileData.sars_clearance_status,
    profileData.pirb_registration_status,
    profileData.nhbrc_certificate_status,
    profileData.saqcc_gas_practitioner_status,
    profileData.electrical_coc_status,
    profileData.gas_coc_status,
    profileData.plumbing_coc_status,
    profileData.electric_fence_coc_status,
    profileData.water_installation_certificate_status,
    profileData.public_liability_insurance_status,
    profileData.qualification_certificate_status,
    profileData.ffc_certificate_status,
    profileData.fsp_license_status,
    profileData.sars_pr_number_status,
    profileData.red_seal_certificate_status,
    profileData.repssa_registration_status,
  ].filter(isPendingStatus).length;
  const locationLabel = [profileData.cityTown, profileData.province].filter(Boolean).join(', ');
  const freshnessLabel = formatFreshnessLabel(profileData.profileContentUpdatedAt || profileData.updatedAt || profileData.createdAt);
  const keyOffering = splitPhrases(profileData.keyServices)[0];
  const heroBestFit = keyOffering || safelyRender(profileData.primaryIndustry) || safelyRender(profileData.restaurantSubcategory) || safelyRender(profileData.jobTitle) || genericProfileType;
  const serviceAreaLabel = [profileData.suburb, profileData.cityTown, profileData.province].filter(Boolean).join(', ');
  const hasIdVerified = isVerifiedStatus(profileData.id_document_status);
  const trustHeadline = hasIdVerified ? 'ID verified on Zamzi Connect' : verifiedSignalsCount > 0 ? 'Public trust signals active' : 'Public profile ready to build trust';
  const searchIntentPhrases = [
    isBusiness && profileData.primaryIndustry ? `${profileData.primaryIndustry} in ${profileData.cityTown || profileData.province || 'South Africa'}` : null,
    isBusiness && profileData.primaryIndustry ? `verified ${String(profileData.primaryIndustry).toLowerCase()} near me` : null,
    isBusiness && !profileData.primaryIndustry && profileData.restaurantSubcategory ? `${profileData.restaurantSubcategory} in ${profileData.cityTown || profileData.province || 'South Africa'}` : null,
    !isBusiness && profileData.jobTitle ? `${profileData.jobTitle} in ${profileData.cityTown || profileData.province || 'South Africa'}` : null,
    keyOffering ? `${keyOffering} in ${profileData.cityTown || profileData.province || 'South Africa'}` : null,
  ].filter(Boolean) as string[];
  const compactTrustFacts = [
    locationLabel ? `Listed in ${locationLabel}` : null,
    verifiedSignalsCount > 0 ? 'Verified trust signals active' : null,
    freshnessLabel,
  ].filter(Boolean) as string[];

  // --- TL;DR (AI summary short) logic ---
  // This logic mirrors the aiSummaryShortContent from the server profile page
  function summarizedServicesText(profile: any): string | undefined {
    if (Array.isArray(profile.pricingServices) && profile.pricingServices.length > 0) {
      const topServices = profile.pricingServices
        .map((service: any) => service?.name)
        .filter(Boolean)
        .slice(0, 3) as string[];
      if (topServices.length) {
        return `Services: ${topServices.join(", ")}`;
      }
    }
    const serviceCategories = Array.isArray(profile.serviceCategories)
      ? profile.serviceCategories.filter(Boolean)
      : typeof profile.serviceCategories === 'string' && profile.serviceCategories
        ? profile.serviceCategories.split(',').map((s: string) => s.trim()).filter(Boolean)
        : [];
    if (serviceCategories.length) {
      return `Services: ${serviceCategories.slice(0, 3).join(", ")}`;
    }
    const skills = Array.isArray(profile.skills)
      ? profile.skills.filter(Boolean)
      : typeof profile.skills === 'string' && profile.skills
        ? profile.skills.split(',').map((s: string) => s.trim()).filter(Boolean)
        : [];
    if (skills.length) {
      return `Skills: ${skills.slice(0, 3).join(", ")}`;
    }
    return undefined;
  }

  const aiSummaryShort = [
    displayName,
    isBusiness ? (profileData.jobTitle || profileData.businessName) : profileData.jobTitle,
    profileData.cityTown || profileData.province,
    summarizedServicesText(profileData),
    'Verified on Zamzi Connect',
  ].filter(Boolean).join(' • ');
  // Best Value badge logic
  let showBestValue = false;
  if (selectedService && Array.isArray(bestValueUserIds) && bestValueUserIds.includes(profileData.userId)) {
    showBestValue = true;
  }
  // Outlier badge logic
  let outlierBadge: React.ReactNode = null;
  if (selectedService && outlierInfo) {
    if (outlierInfo.low.includes(profileData.userId)) {
      outlierBadge = (
        <Badge variant="secondary" className="bg-green-200 text-green-900 border-green-400 text-xs px-2 py-1 mb-1" title="Unusually Cheap">
          Unusually Cheap
        </Badge>
      );
    } else if (outlierInfo.high.includes(profileData.userId)) {
      outlierBadge = (
        <Badge variant="secondary" className="bg-red-200 text-red-900 border-red-400 text-xs px-2 py-1 mb-1" title="Expensive Outlier">
          Expensive
        </Badge>
      );
    }
  }
  const rawBookingCtaText = profileData.bookingCtaText?.trim();
  const shouldShowBookingCta = isBusiness && Boolean(rawBookingCtaText);
  const bookingCtaText = rawBookingCtaText || "Request Booking";
  // Defensive: ensure bookingCtaStyle is always a valid object with color, shape, icon
  const bookingCtaStyle = (profileData.bookingCtaStyle && typeof profileData.bookingCtaStyle === 'object' && profileData.bookingCtaStyle.color && profileData.bookingCtaStyle.shape && profileData.bookingCtaStyle.icon)
    ? profileData.bookingCtaStyle
    : { color: '#001489', shape: 'rounded', icon: 'calendar' };
  const availabilityHours = profileData.availabilityHours || {};
  const blackoutDates = Array.isArray(profileData.blackoutDates) ? profileData.blackoutDates : [];
  const timezone = React.useMemo(() => resolveTimezone(profileData.bookingTimezone), [profileData.bookingTimezone]);
  const blackoutSet = React.useMemo(() => {
    return new Set((blackoutDates as Array<string | null | undefined>).filter((date) => typeof date === 'string' && DATE_PATTERN.test(date)) as string[]);
  }, [blackoutDates]);
  const hasAvailabilityWindows = React.useMemo(() => {
    if (!availabilityHours || typeof availabilityHours !== 'object') {
      return false;
    }
    return Object.values(availabilityHours).some((slot) => slot?.start && slot?.end);
  }, [availabilityHours]);
  const { toast } = useToast();

  // --- Availability Logic ---
  const isTodayBlackout = React.useCallback(() => {
    if (!blackoutSet.size) {
      return false;
    }
    const { dateKey } = getZonedDateParts(new Date(), timezone);
    return blackoutSet.has(dateKey);
  }, [blackoutSet, timezone]);

  const isWithinAvailability = React.useCallback(() => {
    if (!availabilityHours || typeof availabilityHours !== 'object' || !hasAvailabilityWindows) {
      return true;
    }
    const { dayName, minutesSinceMidnight } = getZonedDateParts(new Date(), timezone);
    const window = availabilityHours[dayName];
    if (!window?.start || !window?.end) {
      return true;
    }
    const startMinutes = timeStringToMinutes(window.start);
    const endMinutes = timeStringToMinutes(window.end);
    if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
      return true;
    }
    return minutesSinceMidnight >= startMinutes && minutesSinceMidnight <= endMinutes;
  }, [availabilityHours, hasAvailabilityWindows, timezone]);

  // --- Icon Render ---
  const renderCtaIcon = () => {
    switch (bookingCtaStyle.icon) {
      case 'calendar': return <Calendar className="mr-1 h-4 w-4" />;
      case 'clock': return <span className="mr-1">⏰</span>;
      case 'star': return <span className="mr-1">⭐</span>;
      case 'bolt': return <span className="mr-1">⚡</span>;
      default: return null;
    }
  };

  const handleBookingClick = () => {
    if (!isBusiness || !profileData.userId) {
      return;
    }

    const clickerId = auth.currentUser?.uid ?? null;
    void incrementBookingCtaClick(profileData.userId, clickerId).catch((error) => {
      console.error("Failed to log booking CTA click:", error);
    });

    setIsModalOpen(false);

    const bookingTarget = document.getElementById('instant-book');
    if (bookingTarget) {
      bookingTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', bookingSectionUrl);
      }
      return;
    }

    router.push(bookingSectionUrl);
  };

  const handleEnquiryClick = () => {
    setIsModalOpen(false);

    const enquiryTarget = document.getElementById('lead-inquiry');
    if (enquiryTarget) {
      enquiryTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', enquirySectionUrl);
      }
      return;
    }

    router.push(enquirySectionUrl);
  };

  const handleContactClick = () => {
    if (!profileData.whatsappNumber || !profileData.userId) return;

    const clickerId = auth.currentUser?.uid;
    incrementWhatsAppClick(profileData.userId, clickerId ?? null);
    logContactClick({
      profileId: profileData.userId,
      product: resolveLeadProductFromProfileType(profileData.profileType),
      source: 'profile-whatsapp',
      viewerId: clickerId ?? null,
    });

    let message = "";
    if (isBusiness) {
      const service = profileData.primaryIndustry || "your services";
      message = `Hi ${safelyRender(displayName)}, I found your profile on Zamzi Connect and I'm interested in ${service}. Can you tell me more?`;
    } else {
      const role = profileData.jobTitle || "your skills";
      message = `Hi ${safelyRender(displayName)}, I saw your profile on Zamzi Connect and I'm interested in your skills as a ${role}. Are you available for a chat?`;
    }

    const encodedMessage = encodeURIComponent(message);
    const num = profileData.whatsappNumber.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/${num}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };
  
  const handleModalOpenChange = (open: boolean) => {
    if (open) {
      const viewerId = auth.currentUser?.uid;
      const profileId = profileData.userId;
      if (profileId) {
        incrementProfileView(profileId, viewerId ?? null);
      }
    }
    setIsModalOpen(open);
  };

  const renderProfileImage = (size: number) => {
    const imageClasses = "rounded-full object-cover shadow-lg border-2 border-card bg-inherit";
    const fallBackImageClasses = "rounded-full object-cover shadow-lg border-2 border-white bg-card/70";
    let imageSrc: string | undefined = undefined;
    let fallbackIcon: React.ReactNode = <UserCircle size={size * 0.7} className="text-foreground/70 bg-card/70" />; 
    let aiHint: string = profileData.dataAiHint || "profile avatar";

    if (isBusiness) {
        imageSrc = profileData.companyLogoUrl || (profileData.businessPhotoUrls && profileData.businessPhotoUrls.find(url => url.trim() !== ''));
        aiHint = profileData.dataAiHint || "business office";
        if (!imageSrc) {
            imageSrc = `https://placehold.co/${size}x${size}.png?text=${safelyRender(displayName)?.charAt(0) || 'B'}`;
            aiHint = "business placeholder";
        }
        fallbackIcon = <Building size={size * 0.8} className="text-foreground/70"  style={{fill:"hsl(var(--muted))"}} />;
    } else { 
        if (profileData.profilePhotoUrl) {
            imageSrc = profileData.profilePhotoUrl;
        }
        if (!imageSrc) {
            imageSrc = `https://placehold.co/${size}x${size}.png?text=${safelyRender(displayName)?.charAt(0) || 'P'}`;
            aiHint = "profile placeholder";
        }
    }
    
    if (imageSrc) {
        return (
            <Image
                src={imageSrc}
                alt={`${safelyRender(displayName) || 'Profile'} photo`}
                width={size}
                height={size}
                className={imageClasses}
                data-ai-hint={aiHint}
                onError={(e) => { 
                    e.currentTarget.onerror = null; 
                    e.currentTarget.src = `https://placehold.co/${size}x${size}.png?text=Error`;
                }}
            />
        );
    }
    return (
        <div style={{ height: `${size}px`, width: `${size}px` }} className={`flex items-center justify-center rounded-full ${ fallBackImageClasses }`}  data-ai-hint={aiHint}>
            {fallbackIcon}
        </div>
    );
  };

  // Ref for review section scroll
  const reviewSectionRef = useRef<HTMLDivElement | null>(null);

  // Smooth scroll to review section
  const handleScrollToReviews = () => {
    const el = document.getElementById('profile-reviews-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={handleModalOpenChange}>
      <Card className="w-full max-w-[280px] sm:max-w-xs mx-auto shadow-xl rounded-lg overflow-hidden my-2 flex flex-col bg-white border-none">
        <CardHeader className="bg-white p-4 text-center flex flex-col items-center space-y-2">
          {/* Reviews summary and CTA */}
          {reviewSummary && (
            <div className="w-full flex flex-col items-center mb-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="flex items-center gap-1 text-lg font-bold text-yellow-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i}>
                      <svg width="18" height="18" viewBox="0 0 20 20" fill={i < Math.round(reviewSummary.averageRating) ? '#facc15' : '#e5e7eb'} xmlns="http://www.w3.org/2000/svg"><path d="M10 15.27L16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z"/></svg>
                    </span>
                  ))}
                </span>
                <span className="text-base font-semibold text-foreground">
                  {reviewSummary.averageRating.toFixed(1)}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({reviewSummary.reviewCount} review{reviewSummary.reviewCount !== 1 ? 's' : ''})
                </span>
              </div>
              <Button
                size="lg"
                className="w-full font-bold text-base py-2 px-4 rounded-full bg-yellow-400 hover:bg-yellow-500 text-black shadow-md transition"
                style={{ maxWidth: 220 }}
                onClick={handleScrollToReviews}
                aria-label={canLeaveReview ? "Leave a review" : "View reviews"}
              >
                <svg className="mr-2" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M12 4v16m0 0H3m9 0l-4-4m4 4l4-4"/></svg>
                {canLeaveReview ? 'Leave a review' : 'View reviews'}
              </Button>
            </div>
          )}
          {/* TL;DR / AI Summary Short Box */}
          <div className="w-full mb-2">
            <div className="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-xs text-blue-900 font-semibold shadow-sm flex items-center gap-2" style={{ minHeight: 36 }}>
              <span className="sr-only">Profile Summary:</span>
              <span className="whitespace-pre-line" title={aiSummaryShort}>{aiSummaryShort}</span>
            </div>
          </div>
          {/* Top Reasons to Choose */}
          <div className="w-full mb-1 flex flex-wrap justify-center gap-1">
            {getTopReasons().map((reason, idx) => (
              <Badge
                key={`top-reason-${idx}`}
                variant="secondary"
                className="text-[10px] px-2 py-0.5 bg-green-50 text-green-900 border border-green-200"
              >
                {reason}
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap gap-1 justify-center mb-1">
            {retailTradingGate && !retailTradingGate.open && (
              <div className="w-full flex justify-center">
                <RetailGoLiveBadge gate={retailTradingGate} />
              </div>
            )}
            {showBestValue && (
              <Badge variant="default" className="bg-yellow-400 text-black text-xs px-2 py-1" title="Lowest price for this service">
                Best Value
              </Badge>
            )}
            {outlierBadge}
            {showHiringNowBadge && (
              <Badge
                variant="secondary"
                className="bg-white text-green-800 border-2 border-green-500 text-xs font-semibold px-2 py-1 flex items-center gap-1 shadow-sm"
              >
                <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                <span className="tracking-wide uppercase">Hiring Now</span>
              </Badge>
            )}
            {showFeaturedFounderBadge && isPaidBusinessFounder && (
              <Badge
                variant="secondary"
                className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-medium px-2 py-1"
                title="Featured due to active founder plan"
              >
                Featured founder
              </Badge>
            )}
          </div>
          <div className="mb-2 flex items-center justify-center bg-inherit">
            <Link href={publicProfileUrl} onClick={() => onProfileNavigate?.(profileData.userId)}>
              {renderProfileImage(120)}
            </Link>
          </div>
          <CardTitle className="text-md font-bold wrap-break-word line-clamp-2 mt-1" style={{ color: "#555555" }}>
            <Link href={publicProfileUrl} onClick={() => onProfileNavigate?.(profileData.userId)}>
              {safelyRender(displayName) || "N/A"}
            </Link>
          </CardTitle>
          <CardDescription className="text-xs text-muted-foreground wrap-break-word line-clamp-1">
            {safelyRender(profileData.jobTitle) || genericProfileType}
          </CardDescription>
          <div className="w-full rounded-md border border-sky-100 bg-sky-50 px-3 py-2 text-left">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-800">Best fit</p>
            <p className="mt-1 text-xs font-medium text-slate-800">{heroBestFit}</p>
            <p className="mt-1 text-[11px] text-slate-600">{serviceAreaLabel || 'Available across South Africa'}</p>
          </div>
          <div className="w-full flex flex-wrap items-center justify-center gap-1">
            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-900 text-[10px]">
              {trustHeadline}
            </Badge>
            {freshnessLabel ? (
              <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700 text-[10px]">
                {freshnessLabel}
              </Badge>
            ) : null}
          </div>
          {Array.isArray(resultReasons) && resultReasons.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1 mt-1">
              {resultReasons.slice(0, 3).map((reason, index) => (
                <Badge
                  key={`${profileData.userId}-reason-${index}`}
                  variant="secondary"
                  className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-800 border border-blue-200"
                >
                  {reason}
                </Badge>
              ))}
            </div>
          )}
          <VerificationBadges profileData={profileData} />
          {compactTrustFacts.length > 0 && (
            <div className="w-full mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-left">
              {compactTrustFacts.map((fact) => (
                <p key={fact} className="text-[11px] text-slate-700">
                  {fact}
                </p>
              ))}
            </div>
          )}
          {searchIntentPhrases.length > 0 && (
            <div className="w-full mt-2 flex flex-wrap justify-center gap-1">
              {searchIntentPhrases.slice(0, 2).map((phrase) => (
                <Badge
                  key={phrase}
                  variant="outline"
                  className="text-[10px] px-2 py-0.5 border-blue-200 bg-blue-50 text-blue-900"
                >
                  {phrase}
                </Badge>
              ))}
            </div>
          )}
        </CardHeader>

        <CardContent className="bg-white p-2 grow space-y-1.5">
          {/* Fix-it Squad Specs */}
          {isFixIt && (
            <div className="mb-2 space-y-2">
              {/* Response Time Headline */}
              {(profileData as any).responseTimeMinutes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 flex items-center gap-2">
                  <Clock size={16} className="text-blue-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-blue-900">
                      Arrives in ~{(profileData as any).responseTimeMinutes} min
                    </p>
                    {formatAvailabilityStamp((profileData as any).fixitLastAvailabilityUpdatedAtUTC) && (
                      <p className="text-[10px] text-blue-700 mt-0.5">
                        {formatAvailabilityStamp((profileData as any).fixitLastAvailabilityUpdatedAtUTC)}
                      </p>
                    )}
                  </div>
                  {(profileData as any).isAvailableNow && (
                    <Badge className="bg-green-600 text-white text-[10px]">Available Now</Badge>
                  )}
                </div>
              )}
              {!(profileData as any).responseTimeMinutes && formatAvailabilityStamp((profileData as any).fixitLastAvailabilityUpdatedAtUTC) && (
                <div className="text-[10px] text-slate-500 px-2">
                  {formatAvailabilityStamp((profileData as any).fixitLastAvailabilityUpdatedAtUTC)}
                </div>
              )}
              
              {/* Call-out Fee */}
              {(profileData as any).callOutFee && (
                <div className="text-xs text-slate-600 px-2 py-1 bg-slate-50 rounded border border-slate-200">
                  Call-out fee from <span className="font-bold">R{(profileData as any).callOutFee}</span>
                </div>
              )}
              
              {/* Squad Specs Bar */}
              <SquadSpecsBar
                responseTimeMinutes={(profileData as any).responseTimeMinutes}
                verificationLevel={(profileData as any).verificationLevel}
                insuranceStatus={(profileData as any).insuranceStatus}
                emergencyAvailability={(profileData as any).fixitEmergencyAvailability}
                serviceRadiusKm={(profileData as any).serviceRadiusKm}
                isAvailableNow={(profileData as any).isAvailableNow}
                compact={true}
              />
              
              {/* Licensed Trade Badge */}
              {(profileData as any).fixItContractorClass && (
                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-900 w-full justify-center text-xs">
                  🏆 {(profileData as any).fixItContractorClass}
                </Badge>
              )}
            </div>
          )}
          
          {/* --- Price Fairness Ratings --- */}
          {selectedService && (
            <div className="mb-2 flex flex-col items-center gap-1">
              <div className="flex gap-2 mb-1">
                <button
                  className={`px-2 py-0.5 rounded text-xs border ${userRating === 'Fair' ? 'bg-green-200 border-green-500 text-green-900 font-bold' : 'bg-white border-gray-300 text-gray-700'}`}
                  onClick={() => handleRating('Fair')}
                  disabled={!!userRating}
                  title="Fair price"
                >Fair</button>
                <button
                  className={`px-2 py-0.5 rounded text-xs border ${userRating === 'Bargain' ? 'bg-blue-200 border-blue-500 text-blue-900 font-bold' : 'bg-white border-gray-300 text-gray-700'}`}
                  onClick={() => handleRating('Bargain')}
                  disabled={!!userRating}
                  title="Bargain price"
                >Bargain</button>
                <button
                  className={`px-2 py-0.5 rounded text-xs border ${userRating === 'Expensive' ? 'bg-red-200 border-red-500 text-red-900 font-bold' : 'bg-white border-gray-300 text-gray-700'}`}
                  onClick={() => handleRating('Expensive')}
                  disabled={!!userRating}
                  title="Expensive price"
                >Expensive</button>
              </div>
              <div className="flex gap-2 text-xs text-muted-foreground">
                <span>Fair: {ratingSummary.Fair}</span>
                <span>Bargain: {ratingSummary.Bargain}</span>
                <span>Expensive: {ratingSummary.Expensive}</span>
              </div>
              {userRating && <span className="text-xs text-primary">Thank you for your feedback!</span>}
            </div>
          )}
          {pricingSummary && (
            <div className="rounded-md border border-muted p-2">
              <p className="text-xs font-semibold text-foreground">{pricingSummary.display}</p>
              {pricingSummary.count > 1 && (
                <p className="text-[10px] text-muted-foreground">{pricingSummary.count} services available</p>
              )}
            </div>
          )}
          {displaySummary && (
            <SubCard className="bg-white" title="Summary">
              <p className=" text-muted-foreground line-clamp-3 mb-1.5 text-[11px]">
                {safelyRender(displaySummary)}
              </p>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  className="w-full text-[11px] py-0.5 h-auto flex items-center"
                  style={{ backgroundColor: "#0077B6", color: "hsl(var(--accent-foreground))" }}
                >
                  <ChevronDown className="h-3 w-3 bg-inherit" style={{ fill: 'white', stroke: 'white' }}/>
                  <span className="grow text-center bg-inherit text-white">
                    View More
                  </span>
                  <span className="w-3 opacity-0">
                    <ChevronUp className="h-3 w-3 bg-inherit" />
                  </span>
                </Button>
              </DialogTrigger>
            </SubCard>
          )}
        </CardContent>

        <CardFooter className="flex flex-col items-center space-y-1.5 p-2 border-t mt-auto">
          {isFixIt ? (
            // Fix-it dispatch-focused CTAs
            <>
              <Button
                type="button"
                size="sm"
                className="w-full text-[13px] py-2 h-auto flex items-center justify-center gap-2 font-bold shadow-md"
                style={{ backgroundColor: "#ef4444" }}
                onClick={() => router.push(`/fix-it/request?proId=${profileData.userId}`)}
              >
                <Zap size={16} />
                Request a Fix
              </Button>
              {!isRetailGated && profileData.whatsappNumber && (
                <Button
                  type="button"
                  size="sm"
                  className="w-full text-[11px] py-1.5 h-auto"
                  style={{ backgroundColor: "#22c55e" }}
                  onClick={handleContactClick}
                >
                  <MessageSquare size={14} className="mr-1" />
                  Chat on WhatsApp
                </Button>
              )}
              <Button
                size="sm"
                className="bg-slate-200 text-slate-700 w-full text-[11px] py-0.5 h-auto hover:bg-slate-300"
                type="button"
                onClick={handleEnquiryClick}
              >
                More Info
              </Button>
            </>
          ) : (
            // Generic CTAs for non-Fix-it profiles
            <>
              {shouldShowBookingCta && (
                <Button
                  type="button"
                  size="sm"
                  className={`w-full text-[15px] py-2 h-auto flex items-center justify-center gap-2 ${bookingCtaStyle.shape === 'pill' ? 'rounded-full' : bookingCtaStyle.shape === 'square' ? 'rounded-none' : 'rounded-md'} shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-150`}
                  style={{ backgroundColor: bookingCtaStyle.color, color: '#fff', minHeight: 48, minWidth: 120 }}
                  aria-label={bookingCtaText}
                  onClick={handleBookingClick}
                >
                  {renderCtaIcon()}
                  <span className="truncate" style={{ maxWidth: '80vw' }}>{bookingCtaText}</span>
                </Button>
              )}
              <Button
                size="sm"
                className="bg-accent text-white w-full text-[11px] py-0.5 h-auto"
                style={{ backgroundColor: "#001489" }}
                type="button"
                onClick={handleEnquiryClick}
              >
                Send Enquiry
              </Button>
            </>
          )}
        </CardFooter>
      </Card>
      
      <DialogContent className="sm:max-w-md flex flex-col h-full max-h-[92dvh] sm:max-h-[80dvh] overflow-hidden">
          <DialogHeader className="text-left shrink-0">
            <div className="flex items-start gap-4">
              {renderProfileImage(80)}
              <div className="grow">
                <DialogTitle>{safelyRender(displayName)}</DialogTitle>
                <DialogDescription>
                  {safelyRender(profileData.jobTitle) || genericProfileType}
                </DialogDescription>
                 <div className="text-xs text-muted-foreground mt-1 flex items-center">
                    <MapPin size={12} className="mr-1" />
                    {profileData.cityTown && profileData.province ? `${profileData.cityTown}, ${profileData.province}` : profileData.cityTown || profileData.province || 'Location not specified'}
                </div>
                 <VerificationBadges profileData={profileData} />
              </div>
            </div>
          </DialogHeader>
          <ScrollArea className="grow pr-3 -mr-3 sm:pr-6 sm:-mr-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="grid gap-4 py-4 w-full min-w-0">
                <SubCard title="Summary">
                  <div className="px-3 sm:px-4">
                    <p className="text-muted-foreground text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                      {safelyRender(displaySummary)}
                    </p>
                  </div>
                </SubCard>

                {profileData.aboutUs && isBusiness && (
                  <SubCard title="About Us">
                    <p className="text-muted-foreground text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                      {safelyRender(profileData.aboutUs)}
                    </p>
                  </SubCard>
                )}

                {isBusiness && profileData.showPricing && Array.isArray(profileData.pricingServices) && profileData.pricingServices.length > 0 && (
                  <SubCard title="Pricing">
                    <div className="space-y-2">
                      {profileData.pricingServices.map((service, index) => (
                        <div key={`${service.name}-${index}`} className="flex items-center justify-between gap-2 text-sm">
                          <span className="font-medium text-foreground">{service.name}</span>
                          <span className="text-muted-foreground">
                            {service.minPrice !== undefined && service.maxPrice !== undefined
                              ? `R${service.minPrice}–R${service.maxPrice}`
                              : service.minPrice !== undefined
                                ? `From R${service.minPrice}`
                                : service.maxPrice !== undefined
                                  ? `Up to R${service.maxPrice}`
                                  : ""}
                            {service.unit ? ` / ${service.unit}` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  </SubCard>
                )}

                {profileData.fixitShowCalloutFeePublicly && profileData.fixitCalloutFee && profileData.fixitCalloutFee > 0 && (
                  <SubCard title="Service Pricing">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <span className="font-medium text-foreground">Call-out Fee</span>
                        <span className="font-bold text-blue-600">R{profileData.fixitCalloutFee.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}</span>
                      </div>
                    </div>
                  </SubCard>
                )}

                {profileData.companyMission && isBusiness && (
                  <SubCard title="Company Mission">
                    <p className="text-muted-foreground text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                      {safelyRender(profileData.companyMission)}
                    </p>
                  </SubCard>
                )}

                <SubCard title="Details">
                  <DetailItem icon={<UserCircle size={14} />} label="Full Name" value={profileData.username} />
                  <DetailItem icon={<MapPin size={14} />} label="Location" value={profileData.cityTown && profileData.province ? `${profileData.cityTown}, ${profileData.province}` : profileData.cityTown || profileData.province } />
                  {isBusiness && <DetailItem icon={<Globe size={14} />} label="Business Type" value={profileData.businessType} />}
                  {/*
          The street address is withheld on Stay listings only.

          A Stay property is a home. Publishing "14 Botha Avenue" tells anyone
          reading that the people who let it out sleep there, which is exactly
          the exposure POPIA and the tenant's own safety are there to prevent.
          The suburb and town are already shown above as Location, and they are
          what a guest actually needs to know.

          Shops, trades and restaurants keep it: those premises are commercial,
          already publicly findable, and guests arrive at a street address.
        */}
        {mayPublishStreetAddress(profileData) && (
          <DetailItem icon={<Building size={14} />} label="Address" value={getPublishableStreetAddress(profileData)} />
        )}
                  {isBusiness && googleMapsUrl && <DetailItem icon={<ExternalLink size={14} />} label="Google Maps" value={googleMapsUrl} isLink={true} />}
                  
                  {!isBusiness && (
                    <DetailItem icon={<Briefcase size={14} />} label="Employment Status" value={profileData.currentlyEmployed} />
                  )}
                  {isBusiness && <DetailItem icon={<ExternalLink size={14} />} label="Website" value={profileData.website} isLink={true} />}
                                  {isBusiness && socialLinks.length > 0 && (
                                    <SubCard title="Social Links">
                                      <div className="flex flex-wrap gap-2">
                                        {socialLinks.map((link, idx) => (
                                          <DetailItem
                                            key={link.label}
                                            icon={link.icon}
                                            label={link.label}
                                            value={link.value}
                                            isLink={true}
                                          />
                                        ))}
                                      </div>
                                    </SubCard>
                                  )}
                  {isBusiness && <DetailItem icon={<Calendar size={14} />} label="Year Established" value={profileData.yearEstablished} />}
                   {isBusiness && <DetailItem icon={<Building size={14} />} label="Legal Name" value={profileData.companyLegalName} />}
                  {isBusiness && <DetailItem icon={<Building size={14} />} label="Trading Name" value={profileData.tradingName} />}
                   {isBusiness && <DetailItem icon={<Briefcase size={14} />} label="Primary Industry" value={profileData.primaryIndustry} />}
                  {isBusiness && (
                    <DetailItem icon={<UserCircle size={14} />} label="Primary Contact" value={`${safelyRender(profileData.primaryContactPersonName)} (${safelyRender(profileData.primaryContactPersonJobTitle) || 'N/A'})`} />
                  )}
                </SubCard>

                {isBusiness ? (
                  <>
                    <SubCard title="Key Services"><DetailItem icon={<Award size={14} />} label="" value={profileData.keyServices} isList /></SubCard>
                    <SubCard title="Specialized Industries"><DetailItem icon={<Users size={14} />} label="" value={profileData.specializedIndustries} isList /></SubCard>
                    <SubCard title="Service Categories"><DetailItem icon={<Briefcase size={14} />} label="" value={profileData.serviceCategories} isList /></SubCard>
                    <SubCard title="Product Categories"><DetailItem icon={<Info size={14} />} label="" value={profileData.productCategories} isList /></SubCard>
                    {profileData.businessPhotoUrls && profileData.businessPhotoUrls.length > 0 && (
                        <SubCard title="More Photos">
                            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                                {profileData.businessPhotoUrls.filter(url => url && url.trim() !== '').map((src, idx) => (
                                    <div key={`${src}-${idx}`} className="relative aspect-square overflow-hidden rounded">
                                        <Image
                                            src={src}
                                            alt={`Business photo ${idx + 1}`}
                                            fill
                                            sizes="(max-width: 640px) 33vw, 25vw"
                                            loading="lazy"
                                            className="object-cover"
                                            data-ai-hint="business gallery"
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = `https://placehold.co/120x120.png?text=Photo+${idx + 1}`;
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </SubCard>
                    )}
                  </>
                ) : ( 
                  <>
                    <SubCard title="Skills"><div className="bg-inherit"><DetailItem icon={<Award size={14} />} label="" value={profileData.skills} isList /></div></SubCard>
                    <SubCard title="Education"><DetailItem icon={<GraduationCap size={14} />} label="" value={profileData.education} listAsBadges={false} /></SubCard>
                    {profileData.experienceDetails &&
                      <SubCard title="Experience">
                        <p className="text-muted-foreground text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                          {safelyRender(profileData.experienceDetails)}
                        </p>
                      </SubCard>
                    }
                  </>
                )}
            </div>
          </ScrollArea>
          <DialogFooter className="sm:justify-start gap-2 pt-4 border-t mt-auto flex flex-wrap">
            {!isRetailGated && profileData.whatsappNumber && !isFreeProfile && (
              <Button 
                onClick={handleContactClick}
                className="w-full sm:w-auto"
                style={{ backgroundColor: "#25D366", color: "white" }}
              >
                <MessageSquare className="mr-2 h-4 w-4" style={{stroke:"white",fill:"white"}}/> Contact on WhatsApp
              </Button>
            )}
            {isRestaurant && profileData.whatsappNumber && (
              <a
                href={`tel:${profileData.whatsappNumber.replace(/[^+\d]/g, '')}`}
                className="w-full sm:w-auto"
              >
                <Button
                  type="button"
                  className="w-full sm:w-auto"
                  style={{ backgroundColor: "#0077B6", color: "white" }}
                >
                  <Phone className="mr-2 h-4 w-4" style={{stroke:"white"}}/> Call
                </Button>
              </a>
            )}
            {shouldShowBookingCta && (
              <Button
                type="button"
                size="sm"
                className={`w-full sm:w-auto text-[15px] py-2 h-auto flex items-center justify-center gap-2 ${bookingCtaStyle.shape === 'pill' ? 'rounded-full' : bookingCtaStyle.shape === 'square' ? 'rounded-none' : 'rounded-md'} shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-150`}
                style={{ backgroundColor: bookingCtaStyle.color, color: '#fff', minHeight: 48, minWidth: 160 }}
                aria-label={bookingCtaText}
                onClick={() => {
                  if (typeof handleBookingClick === 'function') {
                    handleBookingClick();
                  } else {
                    // fallback: scroll to booking section if available
                    const el = document.getElementById('booking-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                tabIndex={0}
              >
                {renderCtaIcon()}
                <span className="truncate" style={{ maxWidth: '80vw' }}>{bookingCtaText}</span>
              </Button>
            )}
            <Button
              className="w-full sm:w-auto"
              style={{ backgroundColor: "#001489" }}
              type="button"
              onClick={handleEnquiryClick}
              tabIndex={0}
              aria-label="Send Enquiry"
            >
              Send Enquiry
            </Button>
          </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
